/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantContextEntityLinks.ts
 * PURPOSE: CRM deep links and relation labels for AssistantContextPanel.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 55 / 150 lines
 */

import type {
  AssistantFocusEntity,
  AssistantRelatedEntity,
} from '@shared/lib/frontend/api/assistantContextApi';

type EntityLinkInput = Pick<AssistantFocusEntity, 'entity_type' | 'entity_id'> & {
  metadata?: Record<string, unknown>;
};

export function entityPath(entity: EntityLinkInput): string | null {
  const type = (entity.entity_type || '').toLowerCase();
  const id = (entity.entity_id || '').trim();
  if (!id) return null;
  if (type === 'page') {
    const workspaceId = entity.metadata?.workspace_id;
    if (typeof workspaceId === 'string' && workspaceId.trim()) {
      return `/workspaces/${encodeURIComponent(workspaceId.trim())}/pages/${encodeURIComponent(id)}`;
    }
    return null;
  }
  if (type === 'deal' || type === 'opportunity') return `/crm/deals/${encodeURIComponent(id)}`;
  if (type === 'contact') return `/crm/contacts?contactId=${encodeURIComponent(id)}`;
  if (type === 'account' || type === 'company') return `/crm/accounts?accountId=${encodeURIComponent(id)}`;
  return null;
}

const RELATION_LABELS: Record<string, string> = {
  belongs_to: 'Account',
  works_at: 'Works at',
  has_contact: 'Contact',
  involves_contact: 'Recent activity',
  at_account: 'Linked account',
  parent_of: 'Parent account',
  child_of: 'Child account',
  documents_deal: 'Deal room',
  linked_contact: 'Linked contact',
  linked_company: 'Linked company',
};

export function relationHint(related: AssistantRelatedEntity): string {
  const role = related.metadata?.role_type;
  if (typeof role === 'string' && role.trim()) {
    return role.replace(/_/g, ' ');
  }
  const key = (related.relation_type || '').toLowerCase();
  if (key in RELATION_LABELS) return RELATION_LABELS[key];
  return key.replace(/_/g, ' ') || 'related';
}

export function entityKindLabel(entityType?: string): string {
  return (entityType || 'entity').replace(/_/g, ' ');
}
